import ControlFactory from '@/Factories/ControlFactory';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import RadioBoxGroupViewModelType from '@/ViewModels/Controls/RadioBoxGroupViewModel';
import RadioBoxGroupType from '@/Controls/RadioBoxGroup';
import RadioBoxViewModelType from '@/ViewModels/Controls/RadioBoxViewModel';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import Utilities from '@/Utility/Utilities';
import { IEnumerationRadioButtonFactoryConstructorParameter } from "#/DefaultViewExtensionInterfaces";

export default class EnumerationRadioButtonFactory extends ControlFactory {

    private RadioBoxGroupViewModel = ComponentManager.getViewModel<typeof RadioBoxGroupViewModelType>('RadioBoxGroupViewModel');
    private RadioBoxGroup = ComponentManager.getControl<typeof RadioBoxGroupType>('RadioBoxGroup');
    private RadioBoxViewModel = ComponentManager.getViewModel<typeof RadioBoxViewModelType>('RadioBoxViewModel');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');

    private jQueryControl: JQuery<HTMLElement>;
    private readonly sortAlphabetically: KnockoutObservable<boolean>;
    private readonly orientation: "horizontal" | "vertical";
    /**
     * @override
     */
    public viewModel: RadioBoxGroupViewModelType;

    public constructor(params: IEnumerationRadioButtonFactoryConstructorParameter) {
        super(params);
        this.orientation = this.paramOrDefault(params.direction, 'horizontal');
        this.sortAlphabetically = this.observableOrDefault(params.sortAlphabetically, false);
    }

    /**
     * @override
     */
    control: RadioBoxGroupType;

    /**
     * @override
     */
    public getControl() {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.viewModel = new this.RadioBoxGroupViewModel({
            factory: this,
            isRequired: this.isRequired,
            isValid: this.isValid,
            invalidationText: this.invalidationText,
            orientation: this.orientation,
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            items: this.createItems()
        });

        this.control = new this.RadioBoxGroup(this.viewModel);

        let tuple = this.control.getTuple();
        this.jQueryControl = tuple.control;

        this.addOverwritableToControl(this.jQueryControl, [this.detailViewItem.name]);
        this.registerDefaultValidation([this.detailViewItem.name]);

        return this.jQueryControl;
    }
    
    private createItems(): KnockoutObservableArray<RadioBoxViewModelType>{
        let result = [];
        let name = this.detailViewItem.name;

                for (let value in this.detailViewItem.possiblePDEnumerationValues) {
                    let caption = ResourceManager.getInstance().get(this.detailViewItem.possiblePDEnumerationValues[value]);

            result.push(new this.RadioBoxViewModel({
                name: name,
                value: value,
                parentLocator: this.locator,
                locator: name + '.' + value,
                isEnabled: this.isEnabled,
                checked: this.defineComputed({
                    read: () => {
                        return this.context.genericObject.getObservable(this.detailViewItem.name)();
                    }, 
                    write: (value) => {
                        if (value) {
                            this.context.genericObject.getObservable(this.detailViewItem.name)(value);
                        }
                    }
                }),
                label: new this.LabelViewModel({
                    caption: caption,
                    parentLocator: this.locator,
                    locator: name + '.' + value + '.label'
                }),
                tooltip: new this.LabelViewModel({
                    caption: this.getTooltipCaption(value)
                })
            }));
        }

        if (this.sortAlphabetically()) {
            result = result.sort((a: RadioBoxViewModelType, b: RadioBoxViewModelType) => {
                const aCaption = a.label.caption();
                const bCaption = b.label.caption();

                return aCaption.localeCompare(bCaption);
            });
        }

        return ko.observableArray(result);
    }
    private getTooltipCaption(value: string): string {
        const ergName = this.detailViewItem.possiblePDEnumerationValues[value].replace('_ERGNAME', '_TOOLTIP');
        const optionTooltip = ResourceManager.getInstance().get(ergName);
        if (optionTooltip === ergName || Utilities.isNullOrUndefinedOrBlank(optionTooltip))
            return ResourceManager.getInstance().get(this.detailViewItem.tooltip);
        else {
            return optionTooltip;
        }
    }
}

ComponentManager.setFactory('EnumerationRadioButton', EnumerationRadioButtonFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');